import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  ViewChild,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { defaultTicketIcon, ticketIconsMap } from '../../../../constants/ticket-icons.constant';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-monitoring-tickets-osm-map',
  templateUrl: './monitoring-tickets-osm-map.component.html',
  styleUrls: ['./monitoring-tickets-osm-map.component.scss'],
  standalone: false,
})
export class MonitoringTicketsOsmMapComponent implements AfterViewInit, DoCheck, OnDestroy {
  private static scriptPromise: Promise<any> = null;
  private static clusterScriptPromise: Promise<void> = null;

  @Input() tickets;
  @Input() ticketsMultipleAssign;

  @ViewChild('mapContainer') mapContainer: ElementRef;

  public loading = true;
  public errorText = '';

  private leaflet;
  private map;
  private markersLayer;
  private ticketsSignature = '';
  private ticketsBoundsSignature = '';
  private destroyed = false;
  private moment = require('moment');

  constructor(@Inject(DOCUMENT) private document: Document, private router: Router) {}

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  ngDoCheck(): void {
    if (!this.map) {
      return;
    }

    const signature = this.getTicketsSignature();

    if (signature !== this.ticketsSignature) {
      const boundsSignature = this.getTicketsBoundsSignature();
      const shouldFitBounds = boundsSignature !== this.ticketsBoundsSignature;

      this.ticketsSignature = signature;
      this.ticketsBoundsSignature = boundsSignature;
      this.renderTickets(shouldFitBounds);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initializeMap(): void {
    this.loadLeaflet().then(
      (leaflet) => {
        this.loadMarkerCluster(leaflet)
          .catch(() => undefined)
          .then(() => this.createMap(leaflet));
      },
      () => {
        if (!this.destroyed) {
          this.loading = false;
          this.errorText = 'Не удалось загрузить OpenStreetMap.';
        }
      }
    );
  }

  private createMap(leaflet): void {
    if (this.destroyed || !this.mapContainer) {
      return;
    }

    this.leaflet = leaflet;
    this.map = leaflet.map(this.mapContainer.nativeElement, {
      center: [55.76, 37.64],
      zoom: 10,
      zoomControl: true,
    });
    this.map.attributionControl.setPrefix(false);
    leaflet
      .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      })
      .addTo(this.map);
    this.markersLayer = leaflet.markerClusterGroup
      ? leaflet.markerClusterGroup({
          showCoverageOnHover: false,
          zoomToBoundsOnClick: false,
          spiderfyOnMaxZoom: false,
        })
      : leaflet.layerGroup();
    this.markersLayer.addTo(this.map);

    if (leaflet.markerClusterGroup) {
      this.markersLayer.on('clusterclick', (event) => this.openClusterPopup(event.layer));
    }

    this.ticketsSignature = this.getTicketsSignature();
    this.ticketsBoundsSignature = this.getTicketsBoundsSignature();
    this.loading = false;
    this.renderTickets(true);
  }

  private loadMarkerCluster(leaflet): Promise<void> {
    if (leaflet.markerClusterGroup) {
      return Promise.resolve();
    }

    if (MonitoringTicketsOsmMapComponent.clusterScriptPromise) {
      return MonitoringTicketsOsmMapComponent.clusterScriptPromise;
    }

    this.ensureMarkerClusterStyles();
    MonitoringTicketsOsmMapComponent.clusterScriptPromise = new Promise((resolve, reject) => {
      const existingScript = this.document.querySelector('script[data-leaflet-marker-cluster]');
      const script = (existingScript as HTMLScriptElement) || this.document.createElement('script');

      script.onload = () => (leaflet.markerClusterGroup ? resolve() : reject(new Error('Leaflet MarkerCluster is unavailable')));
      script.onerror = () => reject(new Error('Failed to load Leaflet MarkerCluster'));

      if (!existingScript) {
        script.src = 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-leaflet-marker-cluster', 'true');
        this.document.head.appendChild(script);
      }
    });

    return MonitoringTicketsOsmMapComponent.clusterScriptPromise;
  }

  private ensureMarkerClusterStyles(): void {
    [
      {
        attribute: 'data-leaflet-marker-cluster-css',
        href: 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/MarkerCluster.css',
      },
      {
        attribute: 'data-leaflet-marker-cluster-default-css',
        href: 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css',
      },
    ].forEach((style) => {
      if (this.document.querySelector(`link[${style.attribute}]`)) {
        return;
      }

      const link = this.document.createElement('link');

      link.rel = 'stylesheet';
      link.href = style.href;
      link.setAttribute(style.attribute, 'true');
      this.document.head.appendChild(link);
    });
  }

  private loadLeaflet(): Promise<any> {
    const windowWithLeaflet = window as any;

    if (windowWithLeaflet.L) {
      return Promise.resolve(windowWithLeaflet.L);
    }

    if (MonitoringTicketsOsmMapComponent.scriptPromise) {
      return MonitoringTicketsOsmMapComponent.scriptPromise;
    }

    this.ensureLeafletStyles();
    MonitoringTicketsOsmMapComponent.scriptPromise = new Promise((resolve, reject) => {
      const existingScript = this.document.querySelector('script[data-leaflet-api]');
      const script = (existingScript as HTMLScriptElement) || this.document.createElement('script');

      script.onload = () => {
        const loadedLeaflet = (window as any).L;

        return loadedLeaflet ? resolve(loadedLeaflet) : reject(new Error('Leaflet is unavailable'));
      };
      script.onerror = () => reject(new Error('Failed to load Leaflet'));

      if (!existingScript) {
        script.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-leaflet-api', 'true');
        this.document.head.appendChild(script);
      }
    });

    return MonitoringTicketsOsmMapComponent.scriptPromise;
  }

  private ensureLeafletStyles(): void {
    if (this.document.querySelector('link[data-leaflet-css]')) {
      return;
    }

    const link = this.document.createElement('link');

    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
    link.setAttribute('data-leaflet-css', 'true');
    this.document.head.appendChild(link);
  }

  private renderTickets(shouldFitBounds: boolean): void {
    if (!this.markersLayer) {
      return;
    }

    this.markersLayer.clearLayers();

    const coordinates = [];

    this.getVisibleTickets().forEach((ticket) => {
      const coordinate = this.getCoordinates(ticket);

      if (!coordinate) {
        return;
      }

      const foundTicketIcon = ticketIconsMap.find(
        (candidate) => ticket.ticket_type && candidate.id === ticket.ticket_type.id
      );
      const ticketIcon = foundTicketIcon || defaultTicketIcon;
      const label = (ticket.brigade && ticket.brigade.brigade_name) || ticketIcon.name || 'Заявка';
      const isSelectable = this.canAssignTicket(ticket);
      const isSelected = !!ticket.isChecked;
      const marker = this.leaflet.marker(coordinate, {
        icon: this.createDivIcon(label, ticket.brigade ? ticketIcon.path : ticketIcon.pathGrey, isSelected, isSelectable),
        keyboard: true,
        title: label,
        ticketTypeLabel: ticketIcon.name || 'Заявка',
        ticketNumber: ticket.hd_ticket_number || ticket.id || ticket.hd_ticket_id,
        ticketData: ticket,
        isSelectable,
      });

      if (this.isMapSelectionMode()) {
        marker.on('click', (event) => {
          if (event && event.originalEvent) {
            this.leaflet.DomEvent.stopPropagation(event.originalEvent);
          }
          this.toggleMapTicket(ticket);
        });
      } else {
        marker.bindPopup(this.createTicketPopup(ticket), {
          autoPan: true,
          autoPanPadding: [24, 24],
          closeButton: true,
          maxWidth: 380,
          minWidth: 300,
        });
      }
      marker.addTo(this.markersLayer);
      coordinates.push(coordinate);
    });

    if (!shouldFitBounds) {
      return;
    }

    if (coordinates.length === 1) {
      this.map.setView(coordinates[0], 14);
    } else if (coordinates.length > 1) {
      this.map.fitBounds(this.leaflet.latLngBounds(coordinates), {
        padding: [36, 36],
      });
    }
  }

  private createDivIcon(label: string, iconUrl: string, isSelected: boolean, isSelectable: boolean) {
    const safeLabel = this.escapeHtml(label);
    const safeIconUrl = this.escapeHtml(iconUrl);
    const markerClasses = [
      'monitoring-tickets-osm-map__marker',
      isSelected ? 'monitoring-tickets-osm-map__marker--selected' : '',
      this.isMapSelectionMode() && !isSelectable ? 'monitoring-tickets-osm-map__marker--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return this.leaflet.divIcon({
      className: '',
      html: `
        <div class="${markerClasses}">
          <img src="${safeIconUrl}" alt="" style="display:block;height:35px;width:26px;">
          <span class="monitoring-tickets-osm-map__marker-label">${safeLabel}</span>
          ${isSelected ? '<span class="monitoring-tickets-osm-map__marker-check">✓</span>' : ''}
        </div>
      `,
      iconAnchor: [14, 24],
      iconSize: [Math.max(120, 40 + label.length * 8), 35],
    });
  }

  private createTicketPopup(ticket): HTMLElement {
    const content = this.document.createElement('div');
    const title = this.document.createElement('div');
    const numberRow = this.document.createElement('div');
    const numberLabel = this.document.createElement('span');
    const link = this.document.createElement('a');
    const ticketId = ticket && (ticket.id || ticket.hd_ticket_id);
    const url = ticketId ? '/' + TICKETS_PATHS.ticketLink + '/' + ticketId : '';

    content.style.color = '#172b4d';
    content.style.fontFamily = 'Arial, sans-serif';
    content.style.fontSize = '13px';
    content.style.lineHeight = '18px';

    title.textContent = (ticket.ticket_type && ticket.ticket_type.name) || ticket.ticket_subtype || 'Заявка';
    title.style.fontSize = '16px';
    title.style.fontWeight = '700';
    title.style.marginBottom = '8px';
    content.appendChild(title);

    numberLabel.textContent = 'Номер заявки: ';
    link.textContent = ticket.hd_ticket_number || String(ticketId || '-');
    link.style.color = '#0052cc';
    link.style.cursor = 'pointer';
    link.style.textDecoration = 'underline';

    if (url) {
      link.href = url;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.router.navigateByUrl(url);
      });
    }

    numberRow.appendChild(numberLabel);
    numberRow.appendChild(link);
    content.appendChild(numberRow);
    this.appendPopupRow(content, 'Статус:', ticket.ticket_status && ticket.ticket_status.name);
    this.appendPopupRow(content, 'Бригада:', ticket.brigade && ticket.brigade.brigade_name);
    this.appendPopupRow(content, 'Работы:', this.getTicketTime(ticket));
    this.appendPopupRow(content, 'Адрес:', ticket.hd_address);

    if (this.hasPendingMapAssignment(ticket)) {
      const cancelButton = this.document.createElement('button');

      cancelButton.type = 'button';
      cancelButton.className = 'monitoring-tickets-osm-map__cancel-assignment';
      cancelButton.textContent = 'Отменить назначение';
      cancelButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.cancelMapTicketAssignment(ticket);
        this.map.closePopup();
      });
      content.appendChild(cancelButton);
    }

    return content;
  }

  private openClusterPopup(cluster): void {
    if (!cluster || !this.map) {
      return;
    }

    const content = this.document.createElement('div');
    const tickets = cluster.getAllChildMarkers();
    const isSelectionMode = this.isMapSelectionMode();

    content.className = 'monitoring-tickets-osm-map__cluster-popup';

    tickets.forEach((marker) => {
      const row = this.document.createElement('button');
      const selectionIndicator = this.document.createElement('span');
      const brigade = this.document.createElement('span');
      const ticketDetails = this.document.createElement('span');
      const ticket = marker.options.ticketData;
      const isSelectable = marker.options.isSelectable !== false;
      const ticketTypeLabel = marker.options.ticketTypeLabel || 'Заявка';
      const ticketNumber = marker.options.ticketNumber ? `№${marker.options.ticketNumber}` : '';

      row.type = 'button';
      row.className = 'monitoring-tickets-osm-map__cluster-ticket';
      selectionIndicator.className = 'monitoring-tickets-osm-map__cluster-selection';
      selectionIndicator.textContent = ticket && ticket.isChecked ? '✓' : '○';
      brigade.textContent = marker.options.title || 'Заявка';
      brigade.className = 'monitoring-tickets-osm-map__cluster-brigade';
      ticketDetails.textContent = [ticketTypeLabel, ticketNumber].filter(Boolean).join(' · ');
      ticketDetails.className = 'monitoring-tickets-osm-map__cluster-details';

      if (isSelectionMode) {
        row.classList.toggle('monitoring-tickets-osm-map__cluster-ticket--selected', !!(ticket && ticket.isChecked));
        row.disabled = !isSelectable;
        row.appendChild(selectionIndicator);
      }

      row.appendChild(brigade);
      row.appendChild(ticketDetails);
      row.addEventListener('click', () => {
        if (isSelectionMode) {
          this.toggleMapTicket(ticket);
          selectionIndicator.textContent = ticket && ticket.isChecked ? '✓' : '○';
          row.classList.toggle('monitoring-tickets-osm-map__cluster-ticket--selected', !!(ticket && ticket.isChecked));
          return;
        }

        this.map.closePopup();
        this.map.setView(marker.getLatLng(), 18);
        marker.getPopup().setLatLng(marker.getLatLng()).openOn(this.map);
      });
      content.appendChild(row);
    });

    this.leaflet
      .popup({
        autoPan: true,
        autoPanPadding: [24, 24],
        closeButton: true,
        maxWidth: 360,
        minWidth: 260,
      })
      .setLatLng(cluster.getLatLng())
      .setContent(content)
      .openOn(this.map);
  }

  private appendPopupRow(content: HTMLElement, label: string, value): void {
    if (!value) {
      return;
    }

    const row = this.document.createElement('div');

    row.textContent = `${label} ${value}`;
    row.style.marginTop = '6px';
    content.appendChild(row);
  }

  private getTicketTime(ticket): string {
    if (!ticket || (!ticket.hd_schedule_intbegin && !ticket.hd_schedule_intend)) {
      return '';
    }

    return `${this.formatTime(ticket.hd_schedule_intbegin)} - ${this.formatTime(ticket.hd_schedule_intend)}`;
  }

  private formatTime(value): string {
    return value ? this.moment(value).format('HH:mm') : '-';
  }

  private getVisibleTickets(): any[] {
    const tickets = [];
    const addedIds = {};

    ((this.tickets && this.tickets.dataMatrix) || []).forEach((ticketDate) => {
      this.addTickets(tickets, addedIds, ticketDate && ticketDate.gp_intervals && ticketDate.gp_intervals.tickets);
      ((ticketDate && ticketDate.intervals && ticketDate.intervals.intervals) || []).forEach((interval) => {
        this.addTickets(tickets, addedIds, interval && interval.tickets);
      });
      this.addTickets(tickets, addedIds, ticketDate && ticketDate.edo_intervals && ticketDate.edo_intervals.tickets);
    });

    return tickets;
  }

  private addTickets(result: any[], addedIds, tickets): void {
    (tickets || []).forEach((ticket) => {
      const id = String((ticket && (ticket.id || ticket.hd_ticket_id)) || '');

      if (!ticket || !ticket.isShow || !id || addedIds[id]) {
        return;
      }

      addedIds[id] = true;
      result.push(ticket);
    });
  }

  private getCoordinates(ticket): number[] | null {
    const latitude = this.toCoordinate(ticket && ticket.extra_flags && ticket.extra_flags.latitude);
    const longitude = this.toCoordinate(ticket && ticket.extra_flags && ticket.extra_flags.longitude);

    if (latitude === null || longitude === null || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      return null;
    }

    return [latitude, longitude];
  }

  private getTicketsSignature(): string {
    return this.getVisibleTickets()
      .map((ticket) => {
        const coordinate = this.getCoordinates(ticket) || [];

        return [
          ticket && (ticket.id || ticket.hd_ticket_id),
          coordinate[0],
          coordinate[1],
          ticket && ticket.hd_ticket_number,
          ticket && ticket.brigade && ticket.brigade.id,
          ticket && ticket.isChecked,
          this.isMapSelectionMode(),
          this.hasPendingMapAssignment(ticket),
        ].join(':');
      })
      .join('|');
  }

  private getTicketsBoundsSignature(): string {
    return this.getVisibleTickets()
      .map((ticket) => {
        const coordinate = this.getCoordinates(ticket) || [];

        return [ticket && (ticket.id || ticket.hd_ticket_id), coordinate[0], coordinate[1]].join(':');
      })
      .join('|');
  }

  private isMapSelectionMode(): boolean {
    return !!(this.ticketsMultipleAssign && this.ticketsMultipleAssign.isMapSelectionMode);
  }

  private canAssignTicket(ticket): boolean {
    const canAssignTicket = this.ticketsMultipleAssign && this.ticketsMultipleAssign.canAssignTicket;

    return canAssignTicket ? canAssignTicket(ticket) : false;
  }

  private toggleMapTicket(ticket): void {
    const toggleTicket = this.ticketsMultipleAssign && this.ticketsMultipleAssign.onToggleMapTicket;

    if (toggleTicket && this.canAssignTicket(ticket)) {
      toggleTicket(ticket);
    }
  }

  private hasPendingMapAssignment(ticket): boolean {
    const hasPendingAssignment = this.ticketsMultipleAssign && this.ticketsMultipleAssign.hasPendingMapAssignment;

    return hasPendingAssignment ? hasPendingAssignment(ticket) : false;
  }

  private cancelMapTicketAssignment(ticket): void {
    const cancelAssignment = this.ticketsMultipleAssign && this.ticketsMultipleAssign.cancelMapTicketAssignment;

    if (cancelAssignment) {
      cancelAssignment(ticket);
    }
  }

  private toCoordinate(value): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const coordinate = Number(String(value).replace(',', '.'));

    return Number.isFinite(coordinate) ? coordinate : null;
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
